import React from 'react';
import { type Key, ListLayout, type ListLayoutOptions, Rect, Size } from 'react-aria-components';

/** How a list's options sit: rows on the list's wash, or each its own card (a standalone list of records). */
export type OptionLayout = 'rows' | 'cards';

export const OptionLayoutContext = React.createContext<OptionLayout>('rows');

/**
 * React Aria's ListLayout, laying out a key it has not reached yet (End, a
 * focused key kept mounted) whenever the layout so far stops short of the
 * list's end. React Aria decides that by area, and the overscan widens the
 * area past the content's width: a few pages into a list that loads more,
 * End lands on an option never laid out, nothing scrolls and paging stops.
 */
export interface OptionListLayoutOptions extends ListLayoutOptions {
  /** Room after the last option alone, where `padding` would also inset the first and the sides. */
  endInset?: number;
}

export class OptionListLayout<T = unknown> extends ListLayout<T, OptionListLayoutOptions> {
  private endInset = 0;

  override update(
    invalidationContext: Parameters<ListLayout<T, OptionListLayoutOptions>['update']>[0],
  ) {
    this.endInset = invalidationContext.layoutOptions?.endInset ?? 0;
    super.update(invalidationContext);
  }

  protected override buildCollection(offset?: number) {
    const nodes = super.buildCollection(offset);
    if (this.endInset && this.virtualizer?.collection.size) {
      this.contentSize = new Size(this.contentSize.width, this.contentSize.height + this.endInset);
    }
    return nodes;
  }

  private layoutToTheEnd() {
    const isVertical = this.orientation !== 'horizontal';
    const end = isVertical ? this.requestedRect.maxY : this.requestedRect.maxX;
    const length = isVertical ? this.contentSize.height : this.contentSize.width;
    if (!this.lastCollection || end >= length) return false;
    this.requestedRect = new Rect(0, 0, Infinity, Infinity);
    this.rootNodes = this.buildCollection();
    this.requestedRect = new Rect(0, 0, this.contentSize.width, this.contentSize.height);
    return true;
  }

  override getLayoutInfo(key: Key) {
    if (!this.layoutNodes.has(key)) this.layoutToTheEnd();
    return super.getLayoutInfo(key);
  }

  protected override layoutIfNeeded(rect: Rect) {
    super.layoutIfNeeded(rect);
    for (const key of this.virtualizer?.persistedKeys ?? []) {
      if (!this.layoutNodes.has(key) && this.layoutToTheEnd()) return;
    }
  }
}
